import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Input'
import {
  emptyLeadSourceForm,
  LEAD_SOURCE_TYPES,
  parseLeadSourceTotalCost,
  type LeadSourceFormValues,
} from '@/components/Configuracao/leadSourceConstants'

type Props = {
  open: boolean
  saving: boolean
  onClose: () => void
  onSave: (values: LeadSourceFormValues) => Promise<boolean>
}

const fieldClass =
  'w-full bg-[var(--bg-main)] border border-[var(--glass-border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-xs outline-none focus:border-accent transition-colors min-h-[44px]'

export function LeadSourceCreateModal({ open, saving, onClose, onSave }: Props) {
  const [form, setForm] = useState<LeadSourceFormValues>(emptyLeadSourceForm)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setForm(emptyLeadSourceForm())
      setError('')
    }
  }, [open])

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (form.name.trim().length < 2) {
      setError('Informe um nome com pelo menos 2 caracteres')
      return
    }
    if (form.totalCost.trim() && parseLeadSourceTotalCost(form.totalCost) === null) {
      setError('Custo total inválido')
      return
    }
    setError('')
    const ok = await onSave(form)
    if (ok) setForm(emptyLeadSourceForm())
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-[2px] p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative w-full max-w-lg rounded-xl border border-[var(--glass-border)] bg-[var(--bg-card)] p-6 shadow-[0_24px_64px_rgba(0,0,0,0.45)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-source-modal-title"
      >
        <div className="flex items-center justify-between mb-5 pb-3 border-b border-[var(--glass-border)]">
          <div>
            <h3 id="lead-source-modal-title" className="text-text-primary font-bold text-sm">
              Nova origem de lead
            </h3>
            <p className="text-[11px] text-text-muted mt-1">
              Campos usados nos formulários de lead e relatórios de origem.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 text-text-muted hover:text-text-primary hover:bg-[var(--glass-bg)]"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-text-muted text-[11px] font-medium uppercase tracking-wider">
              Nome da origem *
            </label>
            <input
              autoFocus
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Ex.: Feira ABC, Google Ads, Parceiro X"
              className={fieldClass}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-text-muted text-[11px] font-medium uppercase tracking-wider">
              Tipo / categoria *
            </label>
            <select
              value={form.type}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  type: e.target.value as LeadSourceFormValues['type'],
                }))
              }
              className={fieldClass}
            >
              {LEAD_SOURCE_TYPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <Textarea
            label="Descrição"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Observações sobre a origem (opcional)"
            rows={3}
          />

          <div className="flex flex-col gap-1.5">
            <label className="text-text-muted text-[11px] font-medium uppercase tracking-wider">
              Custo total (R$)
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={form.totalCost}
              onChange={(e) => setForm((prev) => ({ ...prev, totalCost: e.target.value }))}
              placeholder="Opcional — investimento na campanha/origem"
              className={fieldClass}
            />
          </div>

          <label className="flex items-start gap-2 text-xs text-text-primary cursor-pointer rounded-lg border border-[var(--glass-border)] bg-[var(--bg-main)] p-3">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="mt-0.5"
            />
            <span>
              <strong>Origem ativa</strong>
              <span className="mt-1 block text-text-muted">
                Desmarque para ocultar em novos cadastros sem apagar o histórico.
              </span>
            </span>
          </label>

          {error && <p className="text-[11px] text-red-400">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-text-muted hover:text-text-primary transition-colors min-h-[44px]"
            >
              Cancelar
            </button>
            <Button type="submit" variant="primary" size="sm" loading={saving} disabled={saving}>
              Cadastrar origem
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
